import { Avatar, Box, Button, Stack, Typography, styled } from "@mui/material";

import type { MfaMethodType } from "@/hooks/useMfaResource";

import { MfaSecurityChip } from "./MfaSecurityChip";

export interface MfaMethodButtonProps {
  method: MfaMethodType;
  icon: React.ReactNode;
  title: string;
  description: React.ReactNode;
  isSelected: boolean;
  /** Optional security level tag (e.g. "Most Secure", "Less Secure") */
  securityTag?: {
    label: string;
    variant: "success" | "error";
  };
  /** Optional content rendered below the clickable area, inside the card border */
  extraContent?: React.ReactNode;
  onSelect: (method: MfaMethodType) => void;
  disabled?: boolean;
}

// Styled card wrapper (border moves to outer container for extraContent support)
const CardWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== "isSelected",
})<{ isSelected: boolean }>(({ theme, isSelected }) => ({
  border: isSelected ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  backgroundColor: theme.palette.background.paper,
}));

const MethodButton = styled(Button)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(1.25),
  height: "auto",
  minHeight: "unset",
  whiteSpace: "normal",
  border: "none",
  borderRadius: 0,
  textAlign: "left",
  justifyContent: "flex-start",
  backgroundColor: "transparent",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

/**
 * Reusable MFA method selection button.
 * Used in both setup-mfa.tsx and profile ChangeMFAPanel.
 */
export const MfaMethodButton: React.FC<MfaMethodButtonProps> = ({
  method,
  icon,
  title,
  description,
  isSelected,
  securityTag,
  extraContent,
  onSelect,
  disabled = false,
}) => {
  return (
    <CardWrapper isSelected={isSelected}>
      <MethodButton onClick={() => onSelect(method)} disabled={disabled} variant="text" fullWidth>
        {/* Icon Avatar */}
        <Avatar variant={isSelected ? undefined : "outlined"} color={isSelected ? "primary" : undefined}>
          {icon}
        </Avatar>

        {/* Content Box */}
        <Box flex={1}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            {/* Title */}
            <Typography variant="body2" color={isSelected ? "primary" : "text.hint"}>
              {title}
            </Typography>

            {/* Security Tag */}
            {securityTag && <MfaSecurityChip label={securityTag.label} size="small" tagVariant={securityTag.variant} />}
          </Stack>

          {/* Description */}
          <Typography variant="body2" color="text.hint" component="div">
            {description}
          </Typography>
        </Box>
      </MethodButton>
      {extraContent}
    </CardWrapper>
  );
};
